import { useEffect } from 'react';

interface SeoMeta {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  tags?: string[];
  category?: string;
  noindex?: boolean;
}

const BASE_URL = (import.meta.env.VITE_SITE_URL as string | undefined) ?? 'https://roameurope.blog';
const SITE_NAME = BASE_URL.replace(/^https?:\/\//, '');
const DEFAULT_IMAGE = 'https://public.readdy.ai/ai/img_res/947dce0c499e83a5e5cfe4a26ae22628.jpg';
const DEFAULT_DESCRIPTION = 'Expert relationship advice, dating tips, and love stories. Honest, research-backed writing about love, communication, self-love, and human connection. New articles daily.';

function setMeta(property: string, content: string, isName = false) {
  const attr = isName ? 'name' : 'property';
  let el = document.querySelector(`meta[${attr}="${property}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, property);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function removeMeta(property: string, isName = false) {
  const attr = isName ? 'name' : 'property';
  const el = document.querySelector(`meta[${attr}="${property}"]`);
  if (el) el.remove();
}

function setLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

/**
 * Converts a post thumbnail URL into a proper 1200×630 OG share image.
 */
function toOgImage(imageUrl: string): string {
  if (!imageUrl) return DEFAULT_IMAGE;
  try {
    const url = new URL(imageUrl);
    url.searchParams.set('width', '1200');
    url.searchParams.set('height', '630');
    url.searchParams.set('orientation', 'landscape');
    const seq = url.searchParams.get('seq') ?? 'img';
    url.searchParams.set('seq', `${seq}-og`);
    return url.toString();
  } catch {
    return imageUrl;
  }
}

function buildKeywords(tags: string[], category?: string): string {
  const base = ['relationship advice', 'dating tips', 'love', 'communication', 'self-love'];
  const all = [...new Set([...tags, ...(category ? [category] : []), ...base])];
  return all.slice(0, 10).join(', ');
}

export function useSeoMeta({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  url,
  type = 'website',
  author,
  publishedTime,
  modifiedTime,
  tags = [],
  category,
  noindex = false,
}: SeoMeta) {
  useEffect(() => {
    const fullTitle = title
      ? `${title} — roameurope.blog`
      : `Relationship Advice & Dating Tips — ${SITE_NAME}`;
    const fullUrl = url ? `${BASE_URL}${url}` : BASE_URL;
    const ogImage = toOgImage(image);

    // Page title
    document.title = fullTitle;

    // Robots
    setMeta('robots', noindex
      ? 'noindex, nofollow'
      : 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1', true);

    // Basic meta
    setMeta('description', description, true);
    setMeta('keywords', buildKeywords(tags, category), true);
    if (author) setMeta('author', author, true);

    // Canonical
    setLink('canonical', fullUrl);

    // Open Graph
    setMeta('og:title', fullTitle);
    setMeta('og:description', description);
    setMeta('og:image', ogImage);
    setMeta('og:image:width', '1200');
    setMeta('og:image:height', '630');
    setMeta('og:image:type', 'image/jpeg');
    setMeta('og:image:alt', title ?? SITE_NAME);
    setMeta('og:url', fullUrl);
    setMeta('og:type', type);
    setMeta('og:site_name', SITE_NAME);
    setMeta('og:locale', 'en_US');

    // Article-specific OG
    if (type === 'article') {
      if (publishedTime) setMeta('article:published_time', publishedTime);
      if (modifiedTime) setMeta('article:modified_time', modifiedTime);
      if (author) setMeta('article:author', `${BASE_URL}/about`);
      if (category) setMeta('article:section', category);
      setMeta('article:publisher', 'https://www.facebook.com/roameuropeblog');
      tags.forEach((tag, i) => {
        let el = document.querySelector(`meta[property="article:tag"][data-index="${i}"]`) as HTMLMetaElement | null;
        if (!el) {
          el = document.createElement('meta');
          el.setAttribute('property', 'article:tag');
          el.setAttribute('data-index', String(i));
          document.head.appendChild(el);
        }
        el.setAttribute('content', tag);
      });
    } else {
      // Clean up article-specific tags when not on article page
      removeMeta('article:published_time');
      removeMeta('article:modified_time');
      removeMeta('article:section');
    }

    // Twitter Card
    setMeta('twitter:card', 'summary_large_image', true);
    setMeta('twitter:title', fullTitle, true);
    setMeta('twitter:description', description, true);
    setMeta('twitter:image', ogImage, true);
    setMeta('twitter:image:alt', title ?? SITE_NAME, true);
    setMeta('twitter:site', '@roameuropeblog', true);
    if (author) setMeta('twitter:creator', '@roameuropeblog', true);

    // Last modified
    setMeta('last-modified', modifiedTime ?? new Date().toISOString(), true);
  }, [title, description, image, url, type, author, publishedTime, modifiedTime, tags, category, noindex]);
}
